import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { UserRound } from "lucide-react";
import { bookingPatch } from "@shared/schemas/bookings";
import { ApiError, errorMessage } from "@web/lib/api";
import { fmtPhone, fullName } from "@web/lib/format";
import { scheduleDraft } from "@web/lib/scheduleDraft";
import { useFormValidation } from "@web/lib/formValidation";
import { queryClient, useMe } from "@web/lib/session";
import { recordValidationQuery } from "@web/lib/validation";
import { validateBooking } from "@shared/validation/bookings";
import { blockingIssues, evaluation, parseInput, type ValidationIssue } from "@shared/validation/issues";
import { api, bookingQuery, eventTypesQuery, membersQuery, venuesQuery, type BookingDetail, type Contact } from "@web/lib/queries";
import { BottomSheet, Button, Inline, Picker, Row, RowGroup, Stack, Text, TextArea, TextField, Toggle, Workspace, FormSection, Region, SaveBar, ScheduleEditor, ScheduleOverview, ValidationSummary, UnsavedChanges, fieldFindings, focusValidationField, useToast } from "@web/ui";
import { ContactPicker } from "../sheets/ContactPicker";

type Props = { b: BookingDetail; readOnly: boolean; onContact: (id: string) => void; onPricing: () => void };
function initial(b: BookingDetail, timezone: string) {
  return {
    venueId: b.venueId,
    schedule: scheduleDraft(b.schedule, b, timezone),
    guestCount: b.guestCount?.toString() ?? "",
    eventTypeId: b.eventTypeId ?? "",
    eventName: b.eventName ?? "",
    clientIsCompany: Boolean(b.clientIsCompany),
    clientCompanyName: b.clientCompanyName ?? "",
    bookerMemberId: b.bookerMemberId ?? "",
    assignedMemberId: b.assignedMemberId ?? "",
    notesClient: b.notesClient ?? "",
    notesInternal: b.notesInternal ?? "",
  };
}

/** Event tab: dates + optional times (multi-day), guests, type, name, contacts, booker, assignee, notes → PATCH. */
export function EventTab({ b, readOnly, onContact, onPricing }: Props) {
  const toast = useToast();
  const me = useMe(), timezone = me.organization.timezone;
  const [form, setForm] = useState(() => initial(b, timezone));
  const [saved, setSaved] = useState(() => initial(b, timezone));
  const [base, setBase] = useState(b);
  const [failure, setFailure] = useState<ValidationIssue[]>([]);
  const [checksUnavailable, setChecksUnavailable] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [latest, setLatest] = useState<BookingDetail | null>(null);
  const saving = useRef(false);
  const contactSaving = useRef(false);
  const [contactBusy, setContactBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [picking, setPicking] = useState<"primary" | "secondary" | null>(null);
  const [busy, setBusy] = useState(false);
  const eventTypes = useQuery(eventTypesQuery(true));
  const members = useQuery(membersQuery);
  const venues = useQuery(venuesQuery(true));
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const validation = useQuery(recordValidationQuery("booking", b.id));
  useEffect(() => {
    if (!dirty && !saving.current) { const next = initial(b, timezone); setForm(next); setSaved(next); setBase(b); }
  }, [b, dirty, timezone]);
  const scheduleChanged = JSON.stringify(form.schedule) !== JSON.stringify(saved.schedule);
  const draftRecord = { id: b.id, venueId: form.venueId, contactId: b.contactId, secondaryContactId: b.secondaryContactId, status: b.status, schedule: scheduleChanged ? form.schedule : base.schedule,
    guestCount: form.guestCount === "" ? null : Number(form.guestCount), guestCountMax: form.guestCount !== saved.guestCount ? null : b.guestCountMax, eventTypeId: form.eventTypeId || null,
    assignedMemberId: form.assignedMemberId || null, bookerMemberId: form.bookerMemberId || null };
  const local = validateBooking(draftRecord, { now: new Date().toISOString(), action: "booking.save", before: { ...draftRecord, venueId: base.venueId, schedule: base.schedule, guestCount: base.guestCount, guestCountMax: base.guestCountMax, eventTypeId: base.eventTypeId, assignedMemberId: base.assignedMemberId, bookerMemberId: base.bookerMemberId }, hasPackage: b.lines.some(line => line.kind === "package") });
  const removeBody = parseInput(bookingPatch, { secondaryContactId: null }, { type: "booking", id: b.id }, "booking.save");
  const removeContactValidation = useFormValidation(evaluation([...removeBody.issues, ...validateBooking({ ...b, secondaryContactId: null }, { now: new Date().toISOString(), action: "booking.save", before: b }).issues.filter(issue => issue.fields.includes("secondaryContactId"))], false));
  const findings = evaluation([...local.issues, ...failure], false);
  const memberOptions = useMemo(() => (members.data ?? []).filter((m) => m.active || m.id === b.bookerMemberId || m.id === b.assignedMemberId).map((m) => ({ value: m.id, label: m.name })), [members.data, b]);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => { if (saving.current || contactSaving.current) return; setFailure([]); setChecksUnavailable(false); setForm((f) => ({ ...f, [k]: v })); };

  async function save() {
    if (saving.current || contactSaving.current || !dirty) return;
    const patch: Record<string, unknown> = {};
    if (scheduleChanged) { patch.schedule = form.schedule; patch.expectedScheduleRevision = base.scheduleRevision; }
    if (form.venueId !== saved.venueId) { patch.venueId = form.venueId; patch.expectedScheduleRevision = base.scheduleRevision; }
    if (form.guestCount !== saved.guestCount) patch.guestCount = form.guestCount ? Number(form.guestCount) : null;
    if (form.eventTypeId !== saved.eventTypeId) patch.eventTypeId = form.eventTypeId || null;
    if (form.eventName !== saved.eventName) patch.eventName = form.eventName || null;
    if (form.clientIsCompany !== saved.clientIsCompany) patch.clientIsCompany = form.clientIsCompany;
    if (form.clientCompanyName !== saved.clientCompanyName) patch.clientCompanyName = form.clientCompanyName || null;
    if (form.bookerMemberId !== saved.bookerMemberId) patch.bookerMemberId = form.bookerMemberId || null;
    if (form.assignedMemberId !== saved.assignedMemberId) patch.assignedMemberId = form.assignedMemberId || null;
    if (form.notesClient !== saved.notesClient) patch.notesClient = form.notesClient || null;
    if (form.notesInternal !== saved.notesInternal) patch.notesInternal = form.notesInternal || null;
    const parsed = parseInput(bookingPatch, patch, { type: "booking", id: b.id }, "booking.save");
    if (!parsed.success) { setFailure(parsed.issues); return; }
    if (blockingIssues(local, "booking.save").length) { setFailure(blockingIssues(local, "booking.save")); return; }
    saving.current = true;
    setBusy(true);
    try {
      const updated = await api.patchBooking(b.id, parsed.data);
      setSaved(form);
      setBase({ ...base, ...updated });
      setFailure([]); setChecksUnavailable(false); setConflict(false);
      toast.success("Saved");
    } catch (e) {
      setChecksUnavailable(!(e instanceof ApiError) || e.checksComplete === false || e.status >= 500);
      if (e instanceof ApiError) { setFailure(e.issues ?? []); if (e.status === 409) { setLatest(null); setConflict(true); try { setLatest(await queryClient.fetchQuery({ ...bookingQuery(b.id), staleTime: 0 })); } catch { /* Retain the draft and do not authorize a blind rebase. */ } } }
      toast.error(errorMessage(e));
    } finally {
      saving.current = false;
      setBusy(false);
    }
  }

  async function setContact(which: "primary" | "secondary", c: Contact | null) {
    if (which === "primary" && !c) return;
    await api.patchBooking(b.id, which === "primary" ? { contactId: c!.id } : { secondaryContactId: c?.id ?? null });
    setPicking(null);
    toast.success(c ? "Contact updated" : "Secondary contact removed");
  }

  async function removeSecondary() {
    if (readOnly || saving.current || contactSaving.current || !removeBody.success || !removeContactValidation.begin("booking.save")) return;
    contactSaving.current = true; setContactBusy(true);
    setRemoveError(null);
    try {
      await api.patchBooking(b.id, removeBody.data);
      removeContactValidation.clear();
      toast.success("Secondary contact removed");
    } catch (error) {
      removeContactValidation.reject(error);
      setRemoveError(errorMessage(error));
    } finally {
      contactSaving.current = false; setContactBusy(false); removeContactValidation.finish();
    }
  }

  const ro = readOnly || busy || contactBusy;
  return (
    <Workspace variant="booking">
      <Region area="header"><ValidationSummary result={dirty ? findings : validation.data} local={dirty} saved={!dirty} loading={validation.isFetching} unavailable={checksUnavailable || (!dirty && validation.isError)} onRetry={dirty ? () => void save() : () => void validation.refetch()} onIssue={issue => {
        if (issue.entity.type === "contact" && issue.entity.id) onContact(issue.entity.id);
        else if (issue.entity.type === "booking_line" || issue.fields.includes("lines")) onPricing();
        else focusValidationField(issue);
      }} /></Region>
      <FormSection title="Schedule" variant="schedule"><Picker label="Venue" value={form.venueId} disabled={ro} data-validation-field="venueId" {...fieldFindings(findings.issues, "venueId")} options={(venues.data ?? []).filter(venue => venue.active || venue.id === b.venueId).map(venue => ({ value: venue.id, label: venue.name }))} onChange={value => set("venueId", value)} /><ScheduleEditor value={form.schedule} onChange={value => set("schedule", value)} disabled={ro} issues={dirty ? findings.issues : validation.data?.issues} /></FormSection>
      <Region area="aside"><Stack gap={4}><FormSection title="Event details">
      <Inline gap={2} align="start">
        <TextField label="Guests" type="number" inputMode="numeric" min={0} className="flex-1" value={form.guestCount} disabled={ro} data-validation-field="guestCount" {...fieldFindings(findings.issues, "guestCount")} onChange={(e) => set("guestCount", e.target.value)} />
        <Picker label="Event type" placeholder="—" className="flex-1" value={form.eventTypeId} disabled={ro} onChange={(v) => set("eventTypeId", v)} options={(eventTypes.data ?? []).filter((t) => t.active || t.id === b.eventTypeId).map((t) => ({ value: t.id, label: t.name }))} />
      </Inline>
      <TextField label="Event name" value={form.eventName} disabled={ro} onChange={(e) => set("eventName", e.target.value)} />
      </FormSection>
      <FormSection title="Contacts">
        {(removeContactValidation.unavailable || removeContactValidation.result.issues.length > 0) && <ValidationSummary result={removeContactValidation.result} local unavailable={removeContactValidation.unavailable} context="Secondary contact removal was not confirmed. The saved contact remains visible; retry from its contact menu." />}
        {removeError && <Text tone="danger">{removeError}</Text>}
        <RowGroup>
          <ContactRow label="Primary" c={b.contact} onOpen={onContact} onChange={ro ? undefined : () => setPicking("primary")} />
          <ContactRow label="Secondary" c={b.secondaryContact} onOpen={onContact} onChange={ro ? undefined : () => setPicking("secondary")} onRemove={ro || !b.secondaryContact ? undefined : () => { void removeSecondary(); }} />
        </RowGroup>
        <Toggle label="Client is a company" checked={form.clientIsCompany} disabled={ro} onChange={(v) => set("clientIsCompany", v)} />
        {form.clientIsCompany && (
          <TextField
            label="Company name"
            value={form.clientCompanyName}
            disabled={ro}
            onChange={(e) => set("clientCompanyName", e.target.value)}
            hint={b.contact ? `Signer: ${fullName(b.contact) || "—"}${b.contact.title ? `, ${b.contact.title}` : " (add a title on the contact)"}` : "Signer name and title come from the primary contact"}
          />
        )}
      </FormSection>
      <FormSection title="Team & notes">
      <Inline gap={2} align="start">
        <Picker label="Booker" placeholder="—" className="flex-1" value={form.bookerMemberId} disabled={ro} onChange={(v) => set("bookerMemberId", v)} options={memberOptions} />
        <Picker label="Assignee" placeholder="—" className="flex-1" value={form.assignedMemberId} disabled={ro} onChange={(v) => set("assignedMemberId", v)} options={memberOptions} />
      </Inline>
      <TextArea label="Client notes" hint="Visible on the contract and client link" value={form.notesClient} disabled={ro} onChange={(e) => set("notesClient", e.target.value)} />
      <TextArea label="Internal notes" value={form.notesInternal} disabled={ro} onChange={(e) => set("notesInternal", e.target.value)} />
      </FormSection></Stack></Region>
      {!readOnly && (
        <Region area="header"><SaveBar>
        <Button loading={busy} disabled={!dirty || contactBusy} onClick={save}>
          Save changes
        </Button>
        </SaveBar></Region>
      )}
      <UnsavedChanges dirty={dirty} pending={busy || contactBusy} />
      <BottomSheet open={conflict} onClose={() => setConflict(false)} title="Your draft is still here">
        <Stack gap={3}><Text>The schedule could not be saved. Review the findings and compare the latest saved version before retrying. Your draft has not been replaced.</Text><ValidationSummary result={evaluation(failure, false)} local /><Text variant="headline">Your unsaved schedule</Text><ScheduleOverview value={form.schedule} />{latest ? <><Text variant="headline">Latest saved schedule</Text><ScheduleOverview value={latest.schedule} /><Button variant="secondary" onClick={() => { setBase(old => ({ ...old, scheduleRevision: latest.scheduleRevision })); setConflict(false); }}>Keep my draft using the latest revision</Button><Button variant="destructive" onClick={() => { const next = initial(latest, timezone); setForm(next); setSaved(next); setBase(latest); setFailure([]); setConflict(false); }}>Discard draft and load saved version</Button></> : <Text tone="secondary">Latest saved version is not available yet. Your draft is retained; close this panel and retry when the connection is available.</Text>}<Button variant="secondary" onClick={() => setConflict(false)}>Keep editing my draft</Button></Stack>
      </BottomSheet>
      <BottomSheet open={picking !== null} onClose={() => { if (!contactSaving.current) setPicking(null); }} title={picking === "primary" ? "Change primary contact" : "Secondary contact"}>
        <ContactPicker value={null} onPendingChange={value => { contactSaving.current = value; setContactBusy(value); }} onChange={(c) => { if (picking) return setContact(picking, c); }} />
      </BottomSheet>
    </Workspace>
  );
}

function ContactRow({ label, c, onOpen, onChange, onRemove }: { label: string; c: Contact | null; onOpen: (id: string) => void; onChange?: () => void; onRemove?: () => void }) {
  const [open, setOpen] = useState(false);
  const canAct = Boolean(onChange || onRemove);
  return (
    <>
      <Row
        validationField={label === "Primary" ? "contactId" : "secondaryContactId"}
        icon={<UserRound aria-hidden size={22} />}
        title={c ? fullName(c) || c.email || c.phone || "Contact" : `No ${label.toLowerCase()} contact`}
        subtitle={c ? `${label} · ${[fmtPhone(c.phone), c.email].filter(Boolean).join(" · ")}` : label}
        onClick={c && !canAct ? () => onOpen(c.id) : canAct ? () => (c ? setOpen(true) : onChange?.()) : undefined}
      />
      <BottomSheet open={open} onClose={() => setOpen(false)} title={`${label} contact`}>
        <RowGroup>
          {c && <Row title="View contact" subtitle="Details, consent, all bookings" onClick={() => (setOpen(false), onOpen(c.id))} />}
          {onChange && <Row title={c ? "Change contact" : "Add contact"} onClick={() => (setOpen(false), onChange())} />}
          {onRemove && <Row title="Remove secondary contact" onClick={() => (setOpen(false), onRemove())} />}
        </RowGroup>
      </BottomSheet>
    </>
  );
}
